// Stepper-based tour form for creating and editing tours
// Supports concept (draft) and published modes with publish-readiness validation
// Concept tours: 7 steps (Basics, Schedule, Pricing, Descriptions, Items, Images, Review)
// Published tours: 6 steps (Review step hidden since tour is already published)
// Updated: Removed Review step for published tours, increased stepper icon size for active state

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tour } from '../../types/tours';
import { supabase } from '../../lib/supabase';
import {
  FileText, Clock, DollarSign, Info, Package, Image, ClipboardCheck,
  ChevronLeft, ChevronRight, Check, AlertTriangle, Save, ArrowRight
} from 'lucide-react';
import StepBasics from './tour-steps/StepBasics';
import StepSchedule from './tour-steps/StepSchedule';
import StepPricing from './tour-steps/StepPricing';
import StepDescriptions from './tour-steps/StepDescriptions';
import StepItems from './tour-steps/StepItems';
import StepImages from './tour-steps/StepImages';
import StepReview from './tour-steps/StepReview';
import PublishValidationModal from './PublishValidationModal';
import {
  validateBasics,
  validateSchedule,
  validatePricing,
  validateDescriptions,
  validateItems,
  validateImages,
  validateAllForPublishGrouped,
  PublishValidationGroup,
} from '../../utils/tourValidation';

interface TourFormProps {
  initialData?: Tour;
  mode: 'create' | 'edit';
}

const allSteps = [
  { label: 'Basics', icon: FileText },
  { label: 'Schedule', icon: Clock },
  { label: 'Pricing', icon: DollarSign },
  { label: 'Descriptions', icon: Info },
  { label: 'Items', icon: Package },
  { label: 'Images', icon: Image },
  { label: 'Review', icon: ClipboardCheck },
];

const TourForm: React.FC<TourFormProps> = ({ initialData, mode }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishErrors, setPublishErrors] = useState<PublishValidationGroup[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConcept, setIsConcept] = useState(
    initialData?.status === 'concept' || (mode === 'create')
  );
  const [showPublishModal, setShowPublishModal] = useState(false);

  const [formData, setFormData] = useState<Partial<Tour>>(
    initialData || {
      name: '',
      status: 'concept',
      min_persons: 1,
      departure: '',
      duration: '',
      group_tour_price: null,
      reduced_group_price: null,
      private_price: null,
      activity_level: null,
      availability: '',
      location: '',
      meeting_point: 'Las Vegas Restaurant in Sierpe',
      meeting_point_url: 'https://www.google.com/maps/place/Marisquer%C3%ADa,+Bar+y+Restaurante+Las+Vegas/@8.8590395,-83.4713554,20z/data=!4m6!3m5!1s0x8fa40c2e573829a9:0x59ec9ac7bf795651!8m2!3d8.8590565!4d-83.471756!16s%2Fg%2F1q67d3g6k?entry=ttu&g_ep=EgoyMDI1MDcyOS4wIKXMDSoASAFQAw%3D%3D',
      tour_detail_headline: '',
      tour_detail_description: '',
      tour_card_description: '',
      carousel_images: [],
      gallery_images: [],
      include_items: [],
      bring_items: [],
      commission_type: 'percentage',
      commission_value: 0.085
    }
  );

  const isPublished = !isConcept;

  const steps = isPublished
    ? allSteps.filter(s => s.label !== 'Review')
    : allSteps;

  const getStepStatus = (stepIndex: number): 'complete' | 'warning' | 'empty' => {
    const checks = [
      () => validateBasics(formData),
      () => validateSchedule(formData, isPublished),
      () => validatePricing(formData, isPublished),
      () => validateDescriptions(formData, isPublished),
      () => validateItems(formData),
      () => validateImages(formData, isPublished),
    ];
    if (stepIndex >= checks.length) return 'empty';
    const result = checks[stepIndex]();
    if (result.errors.length > 0) return 'warning';
    if (result.isComplete) return 'complete';
    return 'empty';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let processedValue: string | number | null = value;
    if (type === 'number') {
      processedValue = value === '' ? null : Number(value);
    }
    setFormData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemsChange = (field: 'include_items' | 'bring_items', items: string[]) => {
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const handleImagesChange = (field: 'carousel_images' | 'gallery_images', urls: string[]) => {
    setFormData(prev => ({ ...prev, [field]: urls }));
  };

  const handlePublishToggleRequest = () => {
    if (isConcept) {
      const groups = validateAllForPublishGrouped(formData);
      if (groups.length > 0) {
        setShowPublishModal(true);
        return;
      }
      setShowPublishModal(true);
    } else {
      setIsConcept(true);
      setError(null);
      setPublishErrors([]);
    }
  };

  const handleConfirmPublish = () => {
    setIsConcept(false);
    setError(null);
    setPublishErrors([]);
    setShowPublishModal(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setPublishErrors([]);

    if (!formData.name?.trim()) {
      setError('Tour name is required');
      setCurrentStep(0);
      setLoading(false);
      return;
    }

    if (!isConcept) {
      const groups = validateAllForPublishGrouped(formData);
      if (groups.length > 0) {
        setPublishErrors(groups);
        setLoading(false);
        return;
      }
    }

    const { carousel_images, gallery_images, include_items, bring_items, tour_images, tour_shared_items, ...tourData } = formData;
    const status = isConcept ? 'concept' : 'published';

    const nullableFields = [
      'departure', 'duration', 'location', 'availability',
      'tour_detail_headline', 'tour_detail_description', 'tour_card_description',
      'meeting_point', 'meeting_point_url'
    ];
    const cleaned: Record<string, unknown> = { ...tourData };
    for (const field of nullableFields) {
      if (cleaned[field] === '') cleaned[field] = null;
    }
    if (!cleaned.activity_level) delete cleaned.activity_level;

    try {
      if (mode === 'create') {
        const slug = (formData.name || '').toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-');

        const { data: maxOrder } = await supabase
          .from('tours')
          .select('display_order')
          .order('display_order', { ascending: false })
          .limit(1)
          .maybeSingle();

        const nextOrder = (maxOrder?.display_order ?? -1) + 1;

        const insertData: Record<string, unknown> = {
          ...cleaned,
          status,
          slug,
          display_order: nextOrder,
        };

        const { data: newTour, error: tourError } = await supabase
          .from('tours')
          .insert([insertData])
          .select()
          .single();

        if (tourError) throw new Error(tourError.message);
        if (!newTour) throw new Error('Failed to create tour');

        await saveRelatedData(newTour.id, carousel_images, gallery_images, include_items, bring_items);
      } else {
        const updateData: Record<string, unknown> = { ...cleaned, status };

        const { error: updateError } = await supabase
          .from('tours')
          .update(updateData)
          .eq('id', initialData?.id);

        if (updateError) throw updateError;

        await supabase.from('tour_images').delete().eq('tour_id', initialData?.id);
        await supabase.from('tour_shared_items').delete().eq('tour_id', initialData?.id);
        await saveRelatedData(initialData!.id, carousel_images, gallery_images, include_items, bring_items);
      }

      navigate('/kh-admin/tours');
    } catch (err) {
      console.error('Error saving tour:', err);
      setError('Failed to save tour. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveRelatedData = async (
    tourId: string,
    carousel_images?: string[],
    gallery_images?: string[],
    include_items?: string[],
    bring_items?: string[]
  ) => {
    if (carousel_images?.length) {
      const data = carousel_images.map((url, i) => ({
        tour_id: tourId, image_url: url, image_type: 'carousel', display_order: i
      }));
      const { error } = await supabase.from('tour_images').insert(data);
      if (error) throw error;
    }

    if (gallery_images?.length) {
      const data = gallery_images.map((url, i) => ({
        tour_id: tourId, image_url: url, image_type: 'gallery', display_order: i
      }));
      const { error } = await supabase.from('tour_images').insert(data);
      if (error) throw error;
    }

    const sharedItems = [
      ...(include_items?.map((itemId, i) => ({ tour_id: tourId, item_id: itemId, display_order: i })) || []),
      ...(bring_items?.map((itemId, i) => ({ tour_id: tourId, item_id: itemId, display_order: i })) || [])
    ];
    if (sharedItems.length > 0) {
      const { error } = await supabase.from('tour_shared_items').insert(sharedItems);
      if (error) throw error;
    }
  };

  const canGoNext = () => {
    if (currentStep === 0) return !!formData.name?.trim();
    return true;
  };

  const effectiveStep = Math.min(currentStep, steps.length - 1);

  const goToStep = (step: number) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
      setError(null);
      setPublishErrors([]);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {showPublishModal && (
        <PublishValidationModal
          groups={validateAllForPublishGrouped(formData)}
          onClose={() => setShowPublishModal(false)}
          onGoToStep={goToStep}
          onConfirm={handleConfirmPublish}
        />
      )}

      <div className="bg-[#1F2B1F] rounded-xl p-4 border border-white/10">
        <div className="flex items-center justify-between overflow-x-auto p-2">
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isActive = index === effectiveStep;
            const isPast = index < effectiveStep;
            const Icon = step.icon;

            return (
              <React.Fragment key={step.label}>
                <button
                  type="button"
                  onClick={() => goToStep(index)}
                  className="flex flex-col items-center gap-1.5 min-w-0 group"
                >
                  <div className={`
                    w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200
                    ${isActive
                      ? 'bg-[#E85C0D] text-white scale-110 shadow-lg shadow-[#E85C0D]/30'
                      : status === 'complete'
                        ? 'bg-emerald-500/20 text-emerald-400 group-hover:bg-emerald-500/30'
                        : status === 'warning'
                          ? 'bg-amber-500/20 text-amber-400 group-hover:bg-amber-500/30'
                          : 'bg-white/10 text-white/40 group-hover:bg-white/15 group-hover:text-white/60'
                    }
                  `}>
                    {status === 'complete' && !isActive ? (
                      <Check className="w-5 h-5" />
                    ) : status === 'warning' && !isActive ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`text-xs font-medium whitespace-nowrap transition-colors ${
                    isActive ? 'text-[#E85C0D]' : isPast ? 'text-white/60' : 'text-white/40'
                  }`}>
                    {step.label}
                  </span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-px mx-3 min-w-[20px] transition-colors ${
                    index < effectiveStep ? 'bg-emerald-500/40' : 'bg-white/10'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="min-h-[400px]">
        {effectiveStep === 0 && (
          <StepBasics
            formData={formData}
            isConcept={isConcept}
            onChange={handleChange}
            onConceptToggle={handlePublishToggleRequest}
          />
        )}
        {effectiveStep === 1 && (
          <StepSchedule
            formData={formData}
            isConcept={isConcept}
            onChange={handleChange}
            onCheckboxChange={handleCheckboxChange}
          />
        )}
        {effectiveStep === 2 && (
          <StepPricing
            formData={formData}
            isConcept={isConcept}
            onChange={handleChange}
            onSelectChange={handleSelectChange}
          />
        )}
        {effectiveStep === 3 && (
          <StepDescriptions
            formData={formData}
            isConcept={isConcept}
            onChange={handleChange}
          />
        )}
        {effectiveStep === 4 && (
          <StepItems
            formData={formData}
            onItemsChange={handleItemsChange}
          />
        )}
        {effectiveStep === 5 && (
          <StepImages
            formData={formData}
            onImagesChange={handleImagesChange}
          />
        )}
        {effectiveStep === 6 && (
          <StepReview
            formData={formData}
            isConcept={isConcept}
            onGoToStep={goToStep}
          />
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg text-sm">
          {error}
        </div>
      )}

      {publishErrors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-red-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">
              Cannot publish -- the following issues must be resolved:
            </span>
          </div>
          <div className="p-4 space-y-3">
            {publishErrors.map((group) => (
              <div key={group.step} className="flex items-start gap-3">
                <button
                  onClick={() => goToStep(group.step)}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium text-[#E85C0D] hover:text-[#ff6a0d] transition-colors bg-[#E85C0D]/10 px-2.5 py-1 rounded-md hover:bg-[#E85C0D]/20 mt-0.5"
                >
                  {group.label}
                  <ArrowRight className="w-3 h-3" />
                </button>
                <ul className="space-y-0.5">
                  {group.errors.map((err, i) => (
                    <li key={i} className="text-sm text-red-300/80 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-red-400/50 shrink-0" />
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-white/10">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/kh-admin/tours')}
            className="px-5 py-2.5 bg-white/5 text-white/70 rounded-lg hover:bg-white/10 hover:text-white transition-all text-sm"
          >
            Cancel
          </button>
          {effectiveStep > 0 && (
            <button
              type="button"
              onClick={() => goToStep(effectiveStep - 1)}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-white/5 text-white/70 rounded-lg hover:bg-white/10 hover:text-white transition-all text-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {isConcept && effectiveStep !== steps.length - 1 && (
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || !formData.name?.trim()}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-lg hover:bg-amber-500/25 transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save as Concept'}
            </button>
          )}

          {effectiveStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={() => goToStep(effectiveStep + 1)}
              disabled={!canGoNext()}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-all text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={loading || !formData.name?.trim()}
              className={`flex items-center gap-1.5 px-6 py-2.5 rounded-lg transition-all text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                isConcept
                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                  : 'bg-emerald-500 text-white hover:bg-emerald-600'
              }`}
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : isConcept ? 'Save Concept' : mode === 'create' ? 'Publish Tour' : 'Update Tour'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourForm;
